import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useFlags, useOrg, useRole, useUser } from '@/stores/identity';
import {
  useLocationCount,
  useLocations,
} from '@/stores/locations';
import { LocationStatusBadge } from '@/components/LocationStatusBadge';
import { supabase } from '@/lib/supabase';

/**
 * Home / dashboard.
 *
 * Day 14.5 brand amplification: Stat values rendered in brand navy so the
 * dashboard reads as branded even before the user clicks a primary button.
 */
export default function Home() {
  const user = useUser();
  const role = useRole();
  const org = useOrg();
  const flags = useFlags();

  const status = useLocations((s) => s.status);
  const items = useLocations((s) => s.items);
  const load = useLocations((s) => s.load);
  const count = useLocationCount();

  // Day 15.x: managers see only the locations they manage on Home.
  // Owners and platform admins see everything (default behavior).
  const [managedIds, setManagedIds] = useState<Set<string> | null>(null);
  const isPlatformAdmin = Boolean(flags?.is_platform_admin);

  useEffect(() => {
    if (status === 'idle') void load();
  }, [status, load]);

  useEffect(() => {
    let cancelled = false;
    if (role !== 'manager' || isPlatformAdmin || !user?.id) {
      setManagedIds(null);
      return;
    }
    (async () => {
      const { data, error } = await supabase
        .from('location_managers')
        .select('location_id')
        .eq('user_id', user.id);
      if (cancelled) return;
      if (error) {
        // non-critical: location_managers query failed. Manager sees "no
        // locations" instead of leaking other-location names; safer fallback.
        setManagedIds(new Set());
        return;
      }
      setManagedIds(new Set((data ?? []).map((r) => r.location_id as string)));
    })();
    return () => {
      cancelled = true;
    };
  }, [role, isPlatformAdmin, user?.id]);

  const visibleLocations =
    role === 'manager' && !isPlatformAdmin && managedIds
      ? items.filter((l) => managedIds.has(l.id))
      : items;
  const visibleCount =
    role === 'manager' && !isPlatformAdmin && managedIds
      ? visibleLocations.length
      : count;

  if (status === 'loading' || status === 'idle') {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-muted-foreground">
        Loading...
      </div>
    );
  }

  // First-location onboarding only applies to pharmacy customers. Platform
  // admins (Anthropic / Blockchain PMO operations) own a non-pharmacy org
  // by design — they should never be forced through pharmacy onboarding.
  // They land on a near-empty Home, can hop to /admin from the nav.
  if (status === 'loaded' && count === 0 && !isPlatformAdmin) {
    return <Navigate to="/onboarding/first-location" replace />;
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-3xl space-y-6">
        <div className="space-y-1">
          <p className="text-sm text-muted-foreground">Welcome back,</p>
          <h2 className="text-2xl font-semibold tracking-tight">
            {user?.display_name ?? 'there'}
          </h2>
        </div>

        <section className="rounded-lg border border-border bg-card p-6 space-y-3">
          <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
            Identity
          </h3>
          <dl className="grid grid-cols-3 gap-3 text-sm">
            <Stat label="Role" value={role ?? '—'} />
            <Stat label="Email" value={user?.email ?? '—'} />
            <Stat
              label="Sole owner"
              value={flags?.is_sole_owner ? 'yes' : 'no'}
            />
            <Stat label="Org" value={org?.display_name ?? '—'} />
            <Stat label="Timezone" value={org?.timezone ?? '—'} />
            <Stat label="Status" value={org?.status ?? '—'} />
          </dl>
        </section>

        <section className="rounded-lg border border-border bg-card p-6 space-y-3">
          <div className="flex items-start justify-between">
            <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
              Locations ({visibleCount})
            </h3>
            <Link
              to="/locations"
              className="text-xs underline text-muted-foreground"
            >
              Manage all →
            </Link>
          </div>
          <ul className="space-y-2">
            {visibleLocations.slice(0, 5).map((loc) => (
              <li
                key={loc.id}
                className="flex items-center justify-between gap-3 text-sm"
              >
                <Link
                  to={`/locations/${loc.id}`}
                  className="hover:underline truncate"
                >
                  {loc.name}
                </Link>
                <LocationStatusBadge status={loc.status} />
              </li>
            ))}
          </ul>
          {visibleCount > 5 && (
            <p className="text-xs text-muted-foreground">
              ... and {visibleCount - 5} more on the Locations page.
            </p>
          )}
        </section>

      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd
        className="text-sm font-medium"
        style={{ color: 'hsl(var(--brand-navy))' }}
      >
        {value}
      </dd>
    </div>
  );
}
